"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";

interface ProjectResult { id: number; name: string; status: string }
interface UserResult { id: number; displayName: string; username: string; role: string }
interface TaskResult { projectId: number; projectName: string; deptSlug: string; taskLabel: string }
interface Resp { projects: ProjectResult[]; users: UserResult[]; tasks: TaskResult[] }

const statusLabel: Record<string, string> = {
  planning: "規劃中",
  pending_leader_fill: "待填料",
  pending_pm_review: "PM 審",
  pending_director_close: "總監審",
  pending_finance: "財務簽收",
  completed: "已完成",
  rejected: "已駁回",
  archived: "封存",
};

/**
 * 🆕 2026-10-27 A1 · 全站搜尋 modal · Cmd+K / Ctrl+K 開
 */
export default function SearchModal() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState<Resp>({ projects: [], users: [], tasks: [] });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ⌘K / Ctrl+K 開啟 · 也接 window custom event "open-search"(給 Sidebar 按鈕用)
  useEffect(() => {
    function kb(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    function trigger() { setOpen(true); }
    window.addEventListener("keydown", kb);
    window.addEventListener("open-search", trigger);
    return () => {
      window.removeEventListener("keydown", kb);
      window.removeEventListener("open-search", trigger);
    };
  }, []);

  // 開啟時 focus input
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setData({ projects: [], users: [], tasks: [] }); }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const kw = q.trim();
    if (kw.length === 0) {
      setData({ projects: [], users: [], tasks: [] });
      return;
    }
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setBusy(true);
      try {
        const r = await fetch(`/api/search?q=${encodeURIComponent(kw)}`, { credentials: "include", signal: ac.signal });
        if (!r.ok) return;
        const j = (await r.json()) as Resp;
        if (!ac.signal.aborted) setData(j);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
      } finally {
        setBusy(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const totalHits = data.projects.length + data.users.length + data.tasks.length;

  if (!open) {
    // 掛一個隱形的 keybind hint(可選的小 shortcut button)
    return null;
  }

  const modal = (
    <>
      <div onClick={() => setOpen(false)}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000 }} />
      <div style={{
        position: "fixed", top: "15%", left: "50%", transform: "translateX(-50%)",
        width: "min(600px, 92vw)", maxHeight: "70vh", overflowY: "auto",
        background: "#fff", borderRadius: 10, boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        zIndex: 10001, display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: 14, borderBottom: "1px solid #E5E7EB", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="搜尋專案、負責人、任務..."
            style={{ flex: 1, border: "none", outline: "none", fontSize: 15, padding: "4px 0" }} />
          {busy && <span style={{ fontSize: 11, color: "#94A3B8" }}>搜尋中...</span>}
          <span style={{ fontSize: 10, color: "#94A3B8", padding: "2px 6px", border: "1px solid #CBD5E1", borderRadius: 4 }}>ESC</span>
        </div>

        {q.trim() === "" ? (
          <div style={{ padding: 24, textAlign: "center", color: "#9CA3AF", fontSize: 12 }}>
            輸入關鍵字搜尋 · 支援專案名、專案 ID、負責人姓名、任務名稱<br />
            <span style={{ fontSize: 10 }}>快捷鍵 Cmd/Ctrl + K 開啟</span>
          </div>
        ) : totalHits === 0 && !busy ? (
          <div style={{ padding: 24, textAlign: "center", color: "#9CA3AF", fontSize: 12 }}>
            找不到符合「{q}」的結果
          </div>
        ) : (
          <div style={{ padding: "6px 0" }}>
            {data.projects.length > 0 && (
              <ResultSection title="📁 專案" count={data.projects.length}>
                {data.projects.map((p) => (
                  <Link key={p.id} href={`/projects/${p.id}`} onClick={() => setOpen(false)} style={rowStyle}>
                    <span style={{ fontWeight: 600 }}>#{p.id} {p.name}</span>
                    <span style={{ fontSize: 10, color: "#6B7280" }}>{statusLabel[p.status] ?? p.status}</span>
                  </Link>
                ))}
              </ResultSection>
            )}
            {data.users.length > 0 && (
              <ResultSection title="👤 使用者" count={data.users.length}>
                {data.users.map((u) => (
                  <div key={u.id} style={rowStyle}>
                    <span style={{ fontWeight: 600 }}>{u.displayName}</span>
                    <span style={{ fontSize: 10, color: "#6B7280" }}>{u.username} · {u.role}</span>
                  </div>
                ))}
              </ResultSection>
            )}
            {data.tasks.length > 0 && (
              <ResultSection title="📋 任務" count={data.tasks.length}>
                {data.tasks.map((t, i) => (
                  <Link key={i} href={`/projects/${t.projectId}`} onClick={() => setOpen(false)} style={rowStyle}>
                    <span style={{ fontWeight: 600 }}>{t.taskLabel}</span>
                    <span style={{ fontSize: 10, color: "#6B7280" }}>#{t.projectId} {t.projectName} · {t.deptSlug}</span>
                  </Link>
                ))}
              </ResultSection>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}

const rowStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
  padding: "8px 14px", textDecoration: "none", color: "#111",
  borderTop: "0.5px solid #F3F4F6",
};

function ResultSection({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div style={{ paddingBottom: 4 }}>
      <div style={{ padding: "6px 14px", fontSize: 10, fontWeight: 700, color: "#6B7280", background: "#F9FAFB", letterSpacing: 1 }}>
        {title} <span style={{ fontWeight: 400 }}>({count})</span>
      </div>
      {children}
    </div>
  );
}
